import React from 'react';
import {
  Box,
  VStack,
  HStack,
  Text,
  SimpleGrid,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  Progress,
  Icon,
  Tooltip,
  Divider
} from '@chakra-ui/react';
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import { 
  FiClock, 
  FiMail, 
  FiUser,
  FiTrendingUp,
  FiActivity
} from 'react-icons/fi';

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8'];

const ForwardingAnalytics = ({ stats, onRefresh }) => {
  // Prepare hourly distribution data
  const hourlyData = React.useMemo(() => {
    const hours = Array.from({ length: 24 }, (_, i) => ({
      hour: `${i}h`,
      count: 0
    }));
    
    if (stats?.hourlyDistribution) {
      stats.hourlyDistribution.forEach(item => {
        hours[item._id].count = item.count;
      });
    }
    
    return hours;
  }, [stats?.hourlyDistribution]);

  // Prepare status distribution data for pie chart
  const statusData = React.useMemo(() => {
    if (!stats?.overall) return [];
    
    return [
      { name: 'Respondidos', value: stats.overall.replied, color: '#00C49F' },
      { name: 'Pendentes', value: stats.overall.pending, color: '#FFBB28' },
      { name: 'Em Progresso', value: stats.overall.inProgress, color: '#0088FE' },
      { name: 'Ignorados', value: stats.overall.ignored, color: '#8884D8' }
    ].filter(item => item.value > 0);
  }, [stats?.overall]);

  // Find peak hour
  const peakHour = React.useMemo(() => {
    if (!hourlyData || hourlyData.length === 0) return null;
    
    const max = hourlyData.reduce((prev, current) => 
      (prev.count > current.count) ? prev : current
    );
    
    return max.count > 0 ? max : null;
  }, [hourlyData]);

  // Early return after hooks
  if (!stats) {
    return (
      <Box p={8}>
        <Text textAlign="center" color="gray.500">
          Nenhum dado de análise disponível
        </Text>
      </Box>
    );
  }

  const formatTime = (ms) => {
    if (!ms) return '0s';
    const seconds = Math.floor(ms / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    
    if (hours > 0) {
      return `${hours}h ${minutes % 60}m`;
    } else if (minutes > 0) {
      return `${minutes}m`;
    }
    return `${seconds}s`;
  };

  return (
    <VStack spacing={6} align="stretch">
      {/* Key Metrics */}
      <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={4}>
        <Stat
          bg="white"
          p={4}
          borderRadius="lg"
          borderLeft="4px solid"
          borderLeftColor="blue.400"
          boxShadow="sm"
        >
          <StatLabel display="flex" alignItems="center">
            <Icon as={FiActivity} mr={2} />
            Taxa de Entrega
          </StatLabel>
          <StatNumber>
            {stats.overall && stats.overall.total > 0 
              ? ((stats.overall.total - (stats.overall.bounced || 0)) / stats.overall.total * 100).toFixed(1)
              : 0}%
          </StatNumber>
          <StatHelpText>
            {stats.overall?.total || 0} emails processados
          </StatHelpText>
        </Stat>

        <Stat
          bg="white"
          p={4}
          borderRadius="lg"
          borderLeft="4px solid"
          borderLeftColor="green.400"
          boxShadow="sm"
        >
          <StatLabel display="flex" alignItems="center">
            <Icon as={FiTrendingUp} mr={2} />
            Taxa de Resolução
          </StatLabel>
          <StatNumber>
            {stats.overall && stats.overall.total > 0
              ? ((stats.overall.replied + stats.overall.ignored) / stats.overall.total * 100).toFixed(1)
              : 0}%
          </StatNumber>
          <StatHelpText>
            {(stats.overall?.replied || 0) + (stats.overall?.ignored || 0)} resolvidos
          </StatHelpText>
        </Stat>

        <Stat
          bg="white"
          p={4}
          borderRadius="lg"
          borderLeft="4px solid"
          borderLeftColor="purple.400"
          boxShadow="sm"
        >
          <StatLabel display="flex" alignItems="center">
            <Icon as={FiClock} mr={2} />
            Tempo de Processamento
          </StatLabel>
          <StatNumber>
            {formatTime(stats.overall?.avgProcessingTime)}
          </StatNumber>
          <StatHelpText>Média por email</StatHelpText>
        </Stat>

        <Stat
          bg="white"
          p={4}
          borderRadius="lg"
          borderLeft="4px solid"
          borderLeftColor="orange.400"
          boxShadow="sm"
        >
          <StatLabel display="flex" alignItems="center">
            <Icon as={FiMail} mr={2} />
            Anexos Processados
          </StatLabel>
          <StatNumber>{stats.overall?.totalAttachments || 0}</StatNumber>
          <StatHelpText>
            {stats.overall?.total > 0 
              ? (stats.overall.totalAttachments / stats.overall.total).toFixed(1)
              : 0} por email
          </StatHelpText>
        </Stat>
      </SimpleGrid>

      {/* Charts */}
      <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={6}>
        {/* Hourly Distribution Chart */}
        <Box bg="white" p={4} borderRadius="lg" boxShadow="sm">
          <Text fontWeight="bold" mb={4}>Distribuição por Hora</Text>
          <ResponsiveContainer width="100%" height={250}>
            <BarChart data={hourlyData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="hour" />
              <YAxis />
              <RechartsTooltip />
              <Bar dataKey="count" fill="#0088FE" />
            </BarChart>
          </ResponsiveContainer>
          {peakHour && (
            <HStack mt={2} justify="center">
              <Badge colorScheme="blue">
                Horário de pico: {peakHour.hour} ({peakHour.count} emails)
              </Badge>
            </HStack>
          )}
        </Box>

        {/* Status Distribution Pie Chart */}
        <Box bg="white" p={4} borderRadius="lg" boxShadow="sm">
          <Text fontWeight="bold" mb={4}>Distribuição por Status</Text>
          <ResponsiveContainer width="100%" height={250}>
            <PieChart>
              <Pie
                data={statusData}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                outerRadius={80}
                fill="#8884d8"
                dataKey="value"
              >
                {statusData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <RechartsTooltip />
            </PieChart>
          </ResponsiveContainer>
        </Box>
      </SimpleGrid>

      {/* Top Senders Table */}
      <Box bg="white" borderRadius="lg" boxShadow="sm" overflow="hidden">
        <Box bg="gray.50" px={4} py={2}>
          <Text fontWeight="bold">Principais Remetentes</Text>
        </Box>
        
        <Table variant="simple" size="sm">
          <Thead>
            <Tr>
              <Th>Email</Th>
              <Th isNumeric>Emails Enviados</Th>
              <Th>Assuntos Únicos</Th>
              <Th>Frequência</Th>
            </Tr>
          </Thead>
          <Tbody>
            {stats.topSenders && stats.topSenders.length > 0 ? (
              stats.topSenders.map((sender, index) => (
                <Tr key={sender._id || index} _hover={{ bg: 'gray.50' }}>
                  <Td>
                    <HStack>
                      <Icon as={FiUser} />
                      <Tooltip label={sender._id}>
                        <Text isTruncated maxW="250px">
                          {sender._id}
                        </Text>
                      </Tooltip>
                    </HStack>
                  </Td>
                  <Td isNumeric fontWeight="medium">{sender.count}</Td>
                  <Td>
                    <Badge colorScheme="blue">
                      {sender.subjects.length} assunto{sender.subjects.length !== 1 && 's'}
                    </Badge>
                  </Td>
                  <Td>
                    <Progress
                      value={(sender.count / stats.overall.total) * 100}
                      size="sm"
                      colorScheme="blue"
                      borderRadius="full"
                    />
                    <Text fontSize="xs" color="gray.500" mt={1}>
                      {((sender.count / stats.overall.total) * 100).toFixed(1)}% do total
                    </Text>
                  </Td>
                </Tr>
              ))
            ) : (
              <Tr>
                <Td colSpan={4}>
                  <Text textAlign="center" color="gray.500" py={4}>
                    Nenhum remetente encontrado
                  </Text>
                </Td>
              </Tr>
            )}
          </Tbody>
        </Table>
      </Box>

      {/* Summary Statistics */}
      <Box bg="blue.50" p={4} borderRadius="lg">
        <HStack spacing={8} justify="center" flexWrap="wrap">
          <VStack>
            <Text fontSize="sm" color="gray.600">Total de Emails</Text>
            <Text fontSize="2xl" fontWeight="bold" color="blue.600">
              {stats.overall?.total || 0}
            </Text>
          </VStack>
          <Divider orientation="vertical" h="50px" />
          <VStack>
            <Text fontSize="sm" color="gray.600">Taxa de Resposta</Text>
            <Text fontSize="2xl" fontWeight="bold" color="green.600">
              {stats.overall && stats.overall.total > 0
                ? ((stats.overall.replied / stats.overall.total) * 100).toFixed(1)
                : 0}%
            </Text>
          </VStack>
          <Divider orientation="vertical" h="50px" />
          <VStack>
            <Text fontSize="sm" color="gray.600">Tempo Médio de Resposta</Text>
            <Text fontSize="2xl" fontWeight="bold" color="purple.600">
              {formatTime(stats.overall?.avgResponseTime)}
            </Text>
          </VStack>
        </HStack>
      </Box>
    </VStack>
  );
};

export default ForwardingAnalytics;